import { useState, useEffect } from 'react';
import { Key, Save, Eye, EyeOff, Sparkles, Palette } from 'lucide-react';
import { useBranding } from '../contexts/BrandingContext';

interface SettingsPageProps {
  onClose: () => void;
}

const getDefaultModel = (provider: string): string => {
  switch (provider) {
    case 'gemini':
      return 'gemini-pro';
    case 'openai':
      return 'gpt-3.5-turbo';
    case 'groq':
      return 'llama3-8b-8192';
    default:
      return 'gemini-pro';
  }
};

export function SettingsPage({ onClose }: SettingsPageProps) {
  const { appName, loginHeadline, loginSubtitle, appVersion, footerText, updateBranding } = useBranding();
  const [aiProvider, setAiProvider] = useState(() => localStorage.getItem('ai_provider') || 'gemini');
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('ai_api_key') || localStorage.getItem('openai_api_key') || 'AIzaSyBpz8FhG8hxSqGkf6zK44w9xRvNXk14-as');
  const [aiModel, setAiModel] = useState(() => {
    const storedProvider = localStorage.getItem('ai_provider') || 'gemini';
    return localStorage.getItem('ai_model') || getDefaultModel(storedProvider);
  });
  const [showKey, setShowKey] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [localAppName, setLocalAppName] = useState(appName);
  const [localLoginHeadline, setLocalLoginHeadline] = useState(loginHeadline);
  const [localLoginSubtitle, setLocalLoginSubtitle] = useState(loginSubtitle);
  const [localAppVersion, setLocalAppVersion] = useState(appVersion);
  const [localFooterText, setLocalFooterText] = useState(footerText);

  useEffect(() => {
    setLocalAppName(appName);
    setLocalLoginHeadline(loginHeadline);
    setLocalLoginSubtitle(loginSubtitle);
    setLocalAppVersion(appVersion);
    setLocalFooterText(footerText);
  }, [appName, loginHeadline, loginSubtitle, appVersion, footerText]);

  const handleProviderChange = (provider: string) => {
    setAiProvider(provider);
    setAiModel(getDefaultModel(provider));
  };

  const handleSaveAI = () => {
    localStorage.setItem('ai_provider', aiProvider);
    localStorage.setItem('ai_api_key', apiKey);
    localStorage.setItem('ai_model', aiModel);

    setAiSaved(true);
    setTimeout(() => {
      setAiSaved(false);
    }, 2000);
  };

  const handleSaveBranding = () => {
    updateBranding({
      appName: localAppName,
      loginHeadline: localLoginHeadline,
      loginSubtitle: localLoginSubtitle,
      appVersion: localAppVersion,
      footerText: localFooterText,
    });

    setBrandingSaved(true);
    setTimeout(() => {
      setBrandingSaved(false);
    }, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-8 max-w-2xl w-full my-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">App Branding & Appearance</h3>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Customize display names for events
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Application Name
                </label>
                <input
                  type="text"
                  value={localAppName}
                  onChange={(e) => setLocalAppName(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="The Affiliate Commander"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Login Headline
                </label>
                <input
                  type="text"
                  value={localLoginHeadline}
                  onChange={(e) => setLocalLoginHeadline(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="Welcome Back, Commander"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Login Subtitle
                </label>
                <input
                  type="text"
                  value={localLoginSubtitle}
                  onChange={(e) => setLocalLoginSubtitle(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="Viral Link Generator System"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  App Version
                </label>
                <input
                  type="text"
                  value={localAppVersion}
                  onChange={(e) => setLocalAppVersion(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="v25.0 Ultimate"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Footer Text
                </label>
                <input
                  type="text"
                  value={localFooterText}
                  onChange={(e) => setLocalFooterText(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
                  placeholder="© 2025 Suluhhati.com"
                />
              </div>
            </div>
            <button
              onClick={handleSaveBranding}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              {brandingSaved ? (
                '✓ Saved!'
              ) : (
                <>
                  <Palette className="w-4 h-4" />
                  Save Branding
                </>
              )}
            </button>
          </div>

          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">AI Engine Configuration</h3>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Configure AI provider for content generation
            </p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  AI Provider
                </label>
                <select
                  value={aiProvider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="gemini">Google Gemini (Free & Fast)</option>
                  <option value="openai">OpenAI (Premium)</option>
                  <option value="groq">Groq (Ultra Fast/Free)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  AI Model Name
                </label>
                <input
                  type="text"
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Enter model name"
                />
                <p className="text-slate-500 text-xs mt-2">
                  Edit if provider releases new model
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 pr-12 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder={
                      aiProvider === 'gemini'
                        ? 'AIza...'
                        : aiProvider === 'openai'
                        ? 'sk-...'
                        : 'gsk_...'
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition"
                  >
                    {showKey ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={handleSaveAI}
              disabled={!apiKey}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {aiSaved ? (
                '✓ Saved!'
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save AI Config
                </>
              )}
            </button>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition text-sm"
          >
            Close Settings
          </button>
        </div>
      </div>
    </div>
  );
}
